// src/business/pages/procurement/ProcurementTimeline.jsx
import { useCallback, useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { procurementAPI } from "../../services/businessErpService";
import { PageHeader, EmptyState } from "../../components/erp";
import Panel from "../../components/Panel";

const EVENT_ICONS = {
  "pr.submitted": "📋",
  "pr.approved": "✅",
  "pr.cancelled": "❌",
  "pr.used_for_rfq": "🔗",
  "rfq.sent": "📨",
  "rfq.recipient.sent": "📩",
  "rfq.recipient.viewed": "👀",
  "rfq.recipient.quoted": "💬",
  "rfq.recipient.declined": "🙅",
  "rfq.quote_progressed": "📊",
  "rfq.awarded": "🏆",
  "rfq.converted_to_po": "🔜",
  "quote.submitted": "💰",
  "quote.accepted": "✅",
  "po.created": "📦",
  "po.finance_approved": "💳",
  "po.finance_rejected": "🚫",
  "po.sent_to_supplier": "✈️",
  "po.acknowledged": "👍",
  "po.supplier_declined": "⚠",
  "delivery.created": "🚚",
  "delivery.status.updated": "🚢",
  "receiving.recorded": "📥",
  "inventory.posted": "📊",
};

function TimelineItem({ event }) {
  const icon = EVENT_ICONS[event.action_type] || "•";
  const date = event.created_at ? new Date(event.created_at) : null;
  const actorName = event.actor_name || (event.actor_type === "SUPPLIER" ? "Supplier" : "Business member");
  const entityReference = event.entity_reference || `#${event.entity_id}`;
  const entityLabel = event.entity_label || `${event.entity_type} ${entityReference}`;

  return (
    <div className="relative flex gap-4 pb-6 last:pb-0">
      {/* Line */}
      <div className="flex flex-col items-center">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-sm dark:bg-neutral-800">
          {icon}
        </div>
        <div className="w-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-800 dark:text-neutral-100">
            {(event.action_type || "").replace(/\./g, " ").replace(/_/g, " ")}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{entityLabel}</span>
        </div>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          {actorName}
          {event.actor_role ? ` (${event.actor_role})` : ""}
          {" · "}
          {entityReference}
        </p>
        <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">{event.remarks || "—"}</p>
        {(event.from_status || event.to_status) && (
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            {event.from_status ? `${event.from_status} -> ` : ""}{event.to_status || "—"}
          </p>
        )}
        {date && (
          <p className="mt-1 flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500">
            <Clock className="h-3 w-3" /> {date.toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}

export default function ProcurementTimeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await procurementAPI.timeline({ limit: 50 });
      setEvents(res?.data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement" title="Procurement Timeline" subtitle="Full audit trail of procurement activities" />

      <Panel title="Recent Activity">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <div className="h-8 w-8 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <EmptyState title="No activity yet" description="Procurement events will appear here as orders are processed." />
        ) : (
          <div className="max-h-[600px] overflow-y-auto pr-2">
            {events.map((e, i) => <TimelineItem key={e.id || i} event={e} />)}
          </div>
        )}
      </Panel>
    </div>
  );
}
